import {
  Component,
  OnInit,
  AfterViewInit,
  ElementRef,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { OnboardingStateService } from '../../services/onboarding-state.service';

@Component({
  selector: 'app-signup',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule],
  templateUrl: './signup.component.html',
  styleUrls: ['./signup.component.scss'],
})
export class SignupComponent implements OnInit, AfterViewInit {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private onboardingState = inject(OnboardingStateService);

  @ViewChild('typeSsBtn') typeSsBtnRef?: ElementRef<HTMLButtonElement>;
  @ViewChild('typeVgBtn') typeVgBtnRef?: ElementRef<HTMLButtonElement>;
  @ViewChild('emailInput') emailInputRef?: ElementRef<HTMLInputElement>;
  @ViewChild('stepBForm') stepBFormRef?: ElementRef<HTMLFormElement>;

  userType: 'SS' | 'VG' | null = null;
  email = '';
  password = '';
  confirmPassword = '';
  showPassword = false;
  errorMsg = '';
  invalidFields: Record<string, string> = {};
  missingFieldsList: string[] = [];

  ngOnInit(): void {
    const qType =
      this.route.snapshot.queryParamMap.get('type') ||
      this.route.snapshot.queryParamMap.get('role');
    if (qType) {
      const norm = qType.toUpperCase();
      if (norm === 'VG' || norm === 'VOLUNTEER' || norm === 'VOLUNTARIO') {
        this.selectUserType('VG');
      } else if (
        norm === 'SS' ||
        norm === 'STUDENT' ||
        norm === 'ESTUDIANTE' ||
        norm === 'SERVICIO_SOCIAL'
      ) {
        this.selectUserType('SS');
      }
    }
  }

  ngAfterViewInit(): void {
    if (!this.userType) {
      setTimeout(() => {
        this.typeSsBtnRef?.nativeElement?.focus();
      }, 60);
    } else {
      setTimeout(() => {
        this.emailInputRef?.nativeElement?.focus();
      }, 60);
    }
  }

  selectUserType(type: 'SS' | 'VG'): void {
    this.userType = type;
    this.errorMsg = '';
    this.invalidFields = {};
    this.missingFieldsList = [];
    this.onboardingState.updatePhase1({ userType: type });
    setTimeout(() => {
      this.emailInputRef?.nativeElement?.focus();
    }, 80);
  }

  backToStepA(): void {
    this.userType = null;
    this.errorMsg = '';
    this.invalidFields = {};
    this.missingFieldsList = [];
    setTimeout(() => {
      this.typeSsBtnRef?.nativeElement?.focus();
    }, 60);
  }

  clearFieldError(field: string): void {
    if (this.invalidFields[field]) {
      const next = { ...this.invalidFields };
      delete next[field];
      this.invalidFields = next;
      this.missingFieldsList = Object.values(this.invalidFields);
      if (this.missingFieldsList.length === 0) {
        this.errorMsg = '';
      }
    }
  }

  handleStepAKeyDown(event: KeyboardEvent): void {
    if (
      event.key === 'ArrowDown' ||
      event.key === 'ArrowUp' ||
      event.key === 'ArrowRight' ||
      event.key === 'ArrowLeft'
    ) {
      event.preventDefault();
      if (document.activeElement === this.typeSsBtnRef?.nativeElement) {
        this.typeVgBtnRef?.nativeElement?.focus();
      } else {
        this.typeSsBtnRef?.nativeElement?.focus();
      }
    }
  }

  handleFormKeyDown(event: KeyboardEvent): void {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const formEl = this.stepBFormRef?.nativeElement;
    if (!formEl) return;
    const focusables = Array.from(
      formEl.querySelectorAll<HTMLElement>(
        'input:not([disabled]), button[type="submit"]:not([disabled])',
      ),
    );
    const idx = focusables.indexOf(event.target as HTMLElement);
    if (idx === -1) return;
    event.preventDefault();
    const nextIdx =
      event.key === 'ArrowDown'
        ? (idx + 1) % focusables.length
        : (idx - 1 + focusables.length) % focusables.length;
    focusables[nextIdx]?.focus();
  }

  onSubmit(): void {
    this.errorMsg = '';
    this.invalidFields = {};
    this.missingFieldsList = [];

    if (!this.userType) {
      this.errorMsg = 'Selecciona primero tu modalidad de participación (Servicio Social o Voluntario General).';
      return;
    }

    const cleanEmail = this.email.trim().toLowerCase();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!cleanEmail) {
      this.invalidFields['email'] =
        this.userType === 'SS'
          ? 'Correo electrónico institucional (obligatorio)'
          : 'Correo electrónico personal (obligatorio)';
    } else if (!emailRegex.test(cleanEmail)) {
      this.invalidFields['email'] = 'Correo electrónico con formato válido (ej. usuario@dominio.com)';
    }

    if (!this.password) {
      this.invalidFields['password'] = 'Contraseña (obligatoria, mínimo 8 caracteres)';
    } else if (this.password.length < 8) {
      this.invalidFields['password'] = `Contraseña de mínimo 8 caracteres (actualmente tienes ${this.password.length})`;
    }

    if (!this.confirmPassword) {
      this.invalidFields['confirmPassword'] = 'Confirmación de contraseña (obligatoria)';
    } else if (this.password !== this.confirmPassword) {
      this.invalidFields['confirmPassword'] = 'Confirmación de contraseña (no coincide con la contraseña)';
    }

    this.missingFieldsList = Object.values(this.invalidFields);

    if (this.missingFieldsList.length > 0) {
      this.errorMsg = 'Por favor corrige los campos marcados en rojo antes de continuar a la Fase 1:';
      return;
    }

    // Reiniciar estado de onboarding para este nuevo registro y guardar credenciales validadas
    this.onboardingState.resetForNewRegistration(this.userType, cleanEmail, this.password);
    this.router.navigate(['/onboarding/phase-1']);
  }
}
